import { useEffect, useMemo, useState } from 'react'
import type { UseMutationResult } from '@tanstack/react-query'
import {
  ArrowLeft,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock3,
  LayoutPanelLeft,
  Maximize2,
  Repeat,
  UserRound,
} from 'lucide-react'

import { JobListCard } from '@/components/jobs/job-list-card'
import { JobsDayTimeline } from '@/components/jobs/jobs-day-timeline'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Calendar, CalendarDayButton } from '@/components/ui/calendar'
import {
  countRecurringJobs,
  getRecurrenceOccurrenceHint,
  resolveJobCalendarDate,
  toJobCalendarDateKey,
} from '@/lib/job-list-display'
import { getJobStatusLabel } from '@/lib/job-status'
import {
  getWorkDayEntryStatusBadgeClassName,
  getWorkDayEntryStatusLabel,
} from '@/lib/workday-entry-status'
import {
  buildJobCalendarSchedule,
  getCalendarDaySchedule,
  getCalendarGridDays,
  getCalendarMonthGridBounds,
  type ProjectedJobOccurrence,
} from '@/lib/job-recurrence-calendar'
import { cn } from '@/lib/utils'
import type { Job, WorkDayEntry } from '@/types'
import { useWorkspaceLabels } from '@/contexts/workspace-portal-context'

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const MAX_EVENTS_PER_CELL = 3
const JOBS_CALENDAR_LAYOUT_STORAGE_KEY = 'jobs-calendar-layout'

type JobsCalendarLayout = 'split' | 'full'

type JobsCalendarViewProps = {
  jobs: Job[]
  workDayEntries?: WorkDayEntry[]
  selectedDate: Date
  onSelectDate: (date: Date | undefined) => void
  onVisibleMonthChange?: (month: Date) => void
  isLoading?: boolean
  serviceInstanceId?: string | null
  assignmentReturnPath?: string | null
  relativeTimeNow: number
  onOpen: (job: Job) => void
  onOpenJobById?: (jobId: string | number) => void
  onChat?: (job: Job) => void
  onDelete: (job: Job) => void
  onOrderSelect?: (orderReferenceId: string) => void
  onStatusApplied?: () => void
  lockMutation: UseMutationResult<unknown, unknown, { id: string; lock: boolean }, unknown>
}

type DayCalendarEvent =
  | {
      kind: 'job'
      id: string
      label: string
      timeLabel: string
      job: Job
      isRecurring: boolean
    }
  | {
      kind: 'projected'
      id: string
      label: string
      timeLabel: string
      occurrence: ProjectedJobOccurrence
    }
  | {
      kind: 'workday_entry'
      id: string
      label: string
      timeLabel: string
      jobId: string | number
      entry: WorkDayEntry
    }

function readStoredLayout(): JobsCalendarLayout {
  if (typeof window === 'undefined') {
    return 'split'
  }

  const stored = window.localStorage.getItem(JOBS_CALENDAR_LAYOUT_STORAGE_KEY)
  return stored === 'full' ? 'full' : 'split'
}

function formatOccurrenceTime(value: Date) {
  return value.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatJobTime(job: Job) {
  const scheduleDate = resolveJobCalendarDate(job)
  if (!scheduleDate) {
    return ''
  }
  return formatOccurrenceTime(scheduleDate)
}

function formatWorkDayEntryTime(entry: WorkDayEntry) {
  const plannedStart = entry.planned_start || null
  if (!plannedStart) {
    return ''
  }
  const date = new Date(plannedStart)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  return formatOccurrenceTime(date)
}

function resolveWorkDayEntryDateKey(entry: WorkDayEntry): string | null {
  const plannedStart = String(entry.planned_start || '').trim()
  if (plannedStart) {
    const parsed = new Date(plannedStart)
    if (!Number.isNaN(parsed.getTime())) {
      return toJobCalendarDateKey(parsed)
    }
  }

  const workDate = String(entry.work_date || '').trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(workDate)) {
    return workDate
  }

  return null
}

function formatWorkDayEntryLabel(entry: WorkDayEntry): string {
  const worker = String(entry.worker_name || '').trim()
  const skill = String(entry.worker_skill_label || '').trim()
  if (worker && skill) {
    return `${worker} · ${skill}`
  }
  if (worker) {
    return worker
  }
  if (skill) {
    return skill
  }
  return `Workday Entry #${entry.id}`
}

function getCalendarEventStatusLabel(event: DayCalendarEvent): string {
  if (event.kind === 'job') {
    return getJobStatusLabel(event.job.workflow_status_code ?? event.job.status)
  }

  if (event.kind === 'workday_entry') {
    return event.entry.status_label || getWorkDayEntryStatusLabel(event.entry.status)
  }

  return 'Scheduled'
}

function getCalendarEventStatusTextClass(event: DayCalendarEvent): string {
  if (event.kind === 'job') {
    const status = String(event.job.workflow_status_code ?? event.job.status ?? '').trim()
    if (status === 'completed') return 'text-emerald-700'
    if (status === 'in_progress') return 'text-amber-700'
    if (status === 'cancelled' || status === 'withdrawn') return 'text-rose-700'
    if (status === 'on_hold') return 'text-orange-700'
    if (status === 'assigned') return 'text-indigo-700'
    if (status === 'open' || status === 'scheduled') return 'text-blue-700'
    return 'text-slate-700'
  }

  if (event.kind === 'workday_entry') {
    const status = String(event.entry.status ?? '').trim()
    if (status === 'accepted') return 'text-emerald-700'
    if (status === 'completed') return 'text-emerald-700'
    if (status === 'in_progress') return 'text-amber-700'
    if (status === 'rejected' || status === 'schedule_declined') return 'text-rose-700'
    if (status === 'scheduled' || status === 'pending_acceptance') return 'text-blue-700'
    return 'text-slate-700'
  }

  return 'text-violet-700'
}

function shiftVisibleMonth(month: Date, offset: number) {
  return new Date(month.getFullYear(), month.getMonth() + offset, 1)
}

function buildDayEvents(
  materializedJobs: Job[],
  projectedOccurrences: ProjectedJobOccurrence[],
  workDayEntries: WorkDayEntry[],
  entityLabel: string,
): DayCalendarEvent[] {
  const events: DayCalendarEvent[] = [
    ...materializedJobs.map((job) => ({
      kind: 'job' as const,
      id: `job-${job.id}`,
      label: job.title || `${entityLabel} #${job.id}`,
      timeLabel: formatJobTime(job),
      job,
      isRecurring: Boolean(job.recurrence_rule && job.recurrence_rule !== 'none') || Boolean(job.recurrence_parent_info),
    })),
    ...projectedOccurrences.map((occurrence) => ({
      kind: 'projected' as const,
      id: `projected-${occurrence.seriesRootId}-${occurrence.scheduledStart.toISOString()}`,
      label: occurrence.seriesRoot.title || `${entityLabel} #${occurrence.seriesRootId}`,
      timeLabel: formatOccurrenceTime(occurrence.scheduledStart),
      occurrence,
    })),
    ...workDayEntries.map((entry) => ({
      kind: 'workday_entry' as const,
      id: `workday-entry-${entry.id}`,
      label: formatWorkDayEntryLabel(entry),
      timeLabel: formatWorkDayEntryTime(entry),
      jobId: entry.job,
      entry,
    })),
  ]

  return events.sort((left, right) => left.timeLabel.localeCompare(right.timeLabel))
}

function CalendarLegend({ createdEntityLegend }: { createdEntityLegend: string }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
      <span className="inline-flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full bg-primary" />
        {createdEntityLegend}
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full border border-violet-400 bg-background px-1 text-[9px] font-semibold text-violet-700">
          +1
        </span>
        Recurring slot
      </span>
      <span className="inline-flex items-center gap-1.5">
        <Repeat className="h-3 w-3 text-violet-600" />
        Recurring series
      </span>
    </div>
  )
}

function ProjectedOccurrenceCard({
  occurrence,
  onOpenSeries,
  entityLabel,
}: {
  occurrence: ProjectedJobOccurrence
  onOpenSeries: (job: Job) => void
  entityLabel: string
}) {
  const root = occurrence.seriesRoot
  const hint = getRecurrenceOccurrenceHint(root)

  return (
    <article className="rounded-xl border border-dashed border-violet-300/80 bg-violet-50/40 p-3 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-1.5">
            <Badge variant="outline" className="gap-1 border-violet-300 bg-violet-100/70 text-[10px] text-violet-900">
              <Repeat className="h-3 w-3" />
              {occurrence.ruleLabel}
            </Badge>
            <Badge variant="outline" className="border-violet-200 bg-background/80 text-[10px] text-violet-800">
              Scheduled
            </Badge>
          </div>
          <p className="line-clamp-1 text-sm font-semibold text-foreground">
            {root.title || `${entityLabel} #${root.id}`}
          </p>
          <p className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
            <Clock3 className="h-3.5 w-3.5" />
            {formatOccurrenceTime(occurrence.scheduledStart)}
            {hint ? ` · ${hint}` : ''}
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="shrink-0 border-violet-300 text-violet-900 hover:bg-violet-100"
          onClick={() => onOpenSeries(root)}
        >
          Open series
        </Button>
      </div>
    </article>
  )
}

function SelectedDayDetailPanel({
  selectedDate,
  selectedDateJobs,
  selectedProjectedOccurrences,
  selectedWorkDayEntries,
  isLoading,
  serviceInstanceId,
  assignmentReturnPath,
  relativeTimeNow,
  onOpen,
  onOpenJobById,
  onChat,
  onDelete,
  onOrderSelect,
  onStatusApplied,
  lockMutation,
  emptyHint = 'Select another day in the calendar.',
}: {
  selectedDate: Date
  selectedDateJobs: Job[]
  selectedProjectedOccurrences: ProjectedJobOccurrence[]
  selectedWorkDayEntries: WorkDayEntry[]
  isLoading: boolean
  serviceInstanceId?: string | null
  assignmentReturnPath?: string | null
  relativeTimeNow: number
  onOpen: (job: Job) => void
  onOpenJobById?: (jobId: string | number) => void
  onChat?: (job: Job) => void
  onDelete: (job: Job) => void
  onOrderSelect?: (orderReferenceId: string) => void
  onStatusApplied?: () => void
  lockMutation: UseMutationResult<unknown, unknown, { id: string; lock: boolean }, unknown>
  emptyHint?: string
}) {
  const labels = useWorkspaceLabels()
  const totalSelectedCount = selectedDateJobs.length + selectedProjectedOccurrences.length
  const totalSelectedWithEntries = totalSelectedCount + selectedWorkDayEntries.length

  return (
    <div className="space-y-3">
      <div className="rounded-2xl border border-border/70 bg-muted/20 px-4 py-3">
        <p className="text-sm font-medium text-foreground">
          {selectedDate.toLocaleDateString(undefined, {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        </p>
        <p className="text-xs text-muted-foreground">
          {isLoading
            ? 'Loading schedule...'
            : `${totalSelectedWithEntries} scheduled item${totalSelectedWithEntries === 1 ? '' : 's'} · ${selectedDateJobs.length} ${labels.entityPluralLower} · ${selectedWorkDayEntries.length} ${labels.workDayEntryPluralLower} · ${selectedProjectedOccurrences.length} recurring slots`}
        </p>
      </div>

      {isLoading ? (
        <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="h-40 rounded-xl border border-border/70 bg-muted/30" />
          ))}
        </div>
      ) : totalSelectedWithEntries === 0 ? (
        <div className="flex min-h-48 flex-col items-center justify-center rounded-2xl border border-dashed border-border/70 bg-muted/10 p-8 text-center text-muted-foreground">
          <CalendarDays className="mb-2 h-8 w-8 opacity-60" />
          <p className="font-medium text-foreground">Nothing scheduled on this date</p>
          <p className="mt-1 text-xs">{emptyHint}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {selectedDateJobs.length > 0 ? (
            <JobsDayTimeline
              jobs={selectedDateJobs}
              selectedDate={selectedDate}
              onOpenJob={onOpen}
            />
          ) : null}

          {selectedDateJobs.length > 0 ? (
            <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
              {selectedDateJobs.map((job) => (
                <JobListCard
                  key={job.id}
                  job={job}
                  serviceInstanceId={serviceInstanceId}
                  assignmentReturnPath={assignmentReturnPath}
                  relativeTimeNow={relativeTimeNow}
                  onOpen={() => onOpen(job)}
                  onChat={onChat ? () => onChat(job) : undefined}
                  onDelete={() => onDelete(job)}
                  onOrderSelect={onOrderSelect}
                  onStatusApplied={onStatusApplied}
                  lockMutation={lockMutation}
                />
              ))}
            </div>
          ) : null}

          {selectedProjectedOccurrences.length > 0 ? (
            <div className="space-y-2">
              {selectedDateJobs.length > 0 ? (
                <p className="text-xs font-medium uppercase tracking-wide text-violet-800">
                  Recurring schedule
                </p>
              ) : null}
              <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
                {selectedProjectedOccurrences.map((occurrence) => (
                  <ProjectedOccurrenceCard
                    key={`${occurrence.seriesRootId}-${occurrence.scheduledStart.toISOString()}`}
                    occurrence={occurrence}
                    onOpenSeries={onOpen}
                    entityLabel={labels.entity}
                  />
                ))}
              </div>
            </div>
          ) : null}

          {selectedWorkDayEntries.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-foreground/80">
                {labels.workDayEntryPlural}
              </p>
              <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
                {selectedWorkDayEntries.map((entry) => (
                  <article
                    key={entry.id}
                    className="rounded-xl border border-border/70 bg-background p-3 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="line-clamp-1 text-sm font-semibold text-foreground">
                          {formatWorkDayEntryLabel(entry)}
                        </p>
                        <p className="mt-1 text-[11px] text-muted-foreground">
                          {entry.job_title ? `${entry.job_title} · ` : ''}{labels.entity} #{entry.job}
                        </p>
                        <p className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
                          <Clock3 className="h-3.5 w-3.5" />
                          {entry.planned_start ? formatWorkDayEntryTime(entry) || 'Scheduled' : 'Scheduled'}
                        </p>
                        <div className="mt-2">
                          <Badge
                            variant="outline"
                            className={cn('text-[10px]', getWorkDayEntryStatusBadgeClassName(entry.status))}
                          >
                            {getWorkDayEntryStatusLabel(entry.status)}
                          </Badge>
                        </div>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="shrink-0"
                        onClick={() => onOpenJobById?.(entry.job)}
                      >
                        Open job
                      </Button>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}

function CompactCalendarDayButton({
  day,
  modifiers,
  materializedCount,
  projectedCount,
  recurringCount,
  ...props
}: React.ComponentProps<typeof CalendarDayButton> & {
  materializedCount: number
  projectedCount: number
  recurringCount: number
}) {
  const totalCount = materializedCount + projectedCount
  const hasRecurringJobs = recurringCount > 0 || projectedCount > 0

  return (
    <CalendarDayButton
      day={day}
      modifiers={modifiers}
      {...props}
      className={cn(
        props.className,
        totalCount > 0 && 'border border-primary/20 bg-primary/5',
        hasRecurringJobs && 'border-violet-300/70 bg-violet-50/50 text-violet-950 hover:bg-violet-100/70',
      )}
    >
      {hasRecurringJobs ? (
        <Repeat className="absolute right-0.5 top-0.5 h-2.5 w-2.5 text-violet-600" aria-hidden="true" />
      ) : null}
      <span>{day.date.getDate()}</span>
      {totalCount > 0 ? (
        <span className="mt-0.5 inline-flex max-w-full flex-wrap items-center justify-center gap-0.5">
          {materializedCount > 0 ? (
            <span className="inline-flex min-w-[1rem] items-center justify-center rounded-full bg-primary px-1 text-[9px] font-semibold leading-none text-primary-foreground">
              {materializedCount > 9 ? '9+' : materializedCount}
            </span>
          ) : null}
          {projectedCount > 0 ? (
            <span className="inline-flex min-w-[1rem] items-center justify-center rounded-full border border-violet-400 bg-background px-1 text-[9px] font-semibold leading-none text-violet-700">
              +{projectedCount > 9 ? '9+' : projectedCount}
            </span>
          ) : null}
        </span>
      ) : null}
    </CalendarDayButton>
  )
}

function CalendarEventChip({
  event,
  onOpenJob,
  onOpenJobById,
}: {
  event: DayCalendarEvent
  onOpenJob: (job: Job) => void
  onOpenJobById?: (jobId: string | number) => void
}) {
  const isProjected = event.kind === 'projected'
  const isWorkdayEntry = event.kind === 'workday_entry'
  const statusLabel = getCalendarEventStatusLabel(event)
  const statusTextClass = getCalendarEventStatusTextClass(event)

  return (
    <button
      type="button"
      onClick={(clickEvent) => {
        clickEvent.stopPropagation()
        if (event.kind === 'job') {
          onOpenJob(event.job)
          return
        }
        if (event.kind === 'workday_entry') {
          onOpenJobById?.(event.jobId)
          return
        }
        onOpenJob(event.occurrence.seriesRoot)
      }}
      className={cn(
        'flex w-full min-w-0 items-start gap-1 rounded-md px-1.5 py-1 text-left text-[10px] leading-tight transition hover:brightness-95',
        isProjected
          ? 'border border-dashed border-violet-300/80 bg-violet-50/80 text-violet-950'
          : isWorkdayEntry
            ? 'border border-emerald-200/80 bg-emerald-50/70 text-emerald-950'
          : 'border border-primary/15 bg-primary/10 text-foreground',
      )}
      title={event.label}
    >
      {isProjected || (event.kind === 'job' && event.isRecurring) ? (
        <Repeat className="mt-0.5 h-3 w-3 shrink-0 text-violet-600" aria-hidden="true" />
      ) : isWorkdayEntry ? (
        <UserRound className="mt-0.5 h-3 w-3 shrink-0 text-emerald-700" aria-hidden="true" />
      ) : (
        <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden="true" />
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{event.label}</span>
        {event.timeLabel ? (
          <span className="block truncate text-[9px] text-muted-foreground">{event.timeLabel}</span>
        ) : null}
        {statusLabel ? (
          <span className={cn('block truncate text-[8px] uppercase tracking-wide', statusTextClass)}>
            {statusLabel}
          </span>
        ) : null}
      </span>
    </button>
  )
}

function FullCalendarDayCell({
  date,
  visibleMonth,
  isSelected,
  events,
  onSelectDate,
  onOpenJob,
  onOpenJobById,
}: {
  date: Date
  visibleMonth: Date
  isSelected: boolean
  events: DayCalendarEvent[]
  onSelectDate: (date: Date) => void
  onOpenJob: (job: Job) => void
  onOpenJobById?: (jobId: string | number) => void
}) {
  const isCurrentMonth = date.getMonth() === visibleMonth.getMonth()
  const visibleEvents = events.slice(0, MAX_EVENTS_PER_CELL)
  const hiddenCount = Math.max(0, events.length - visibleEvents.length)
  const isToday = toJobCalendarDateKey(date) === toJobCalendarDateKey(new Date())

  return (
    <button
      type="button"
      onClick={() => onSelectDate(date)}
      className={cn(
        'flex min-h-[7.5rem] flex-col border border-border/60 bg-background p-1.5 text-left transition hover:bg-muted/30 sm:min-h-[8.5rem] md:min-h-[9.5rem]',
        !isCurrentMonth && 'bg-muted/15 text-muted-foreground',
        isSelected && 'ring-2 ring-primary ring-inset',
        isToday && 'bg-primary/5',
      )}
    >
      <div className="mb-1 flex items-center justify-between gap-1">
        <span
          className={cn(
            'inline-flex h-6 min-w-6 items-center justify-center rounded-full text-xs font-semibold',
            isToday && 'bg-primary text-primary-foreground',
          )}
        >
          {date.getDate()}
        </span>
        {events.length > 0 ? (
          <span className="rounded-full bg-muted px-1.5 py-0.5 text-[9px] font-medium text-muted-foreground">
            {events.length}
          </span>
        ) : null}
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-hidden">
        {visibleEvents.map((event) => (
          <CalendarEventChip
            key={event.id}
            event={event}
            onOpenJob={onOpenJob}
            onOpenJobById={onOpenJobById}
          />
        ))}
        {hiddenCount > 0 ? (
          <span className="px-1 text-[9px] font-medium text-muted-foreground">+{hiddenCount} more</span>
        ) : null}
      </div>
    </button>
  )
}

function useCalendarSchedule(jobs: Job[], visibleMonth: Date) {
  const monthSchedule = useMemo(
    () => buildJobCalendarSchedule(jobs, visibleMonth),
    [jobs, visibleMonth],
  )

  const scheduleByDate = useMemo(
    () => new Map(monthSchedule.map((day) => [day.dateKey, day])),
    [monthSchedule],
  )

  const monthStats = useMemo(() => {
    let materialized = 0
    let projected = 0
    for (const day of monthSchedule) {
      const dayDate = new Date(`${day.dateKey}T12:00:00`)
      if (dayDate.getMonth() !== visibleMonth.getMonth() || dayDate.getFullYear() !== visibleMonth.getFullYear()) {
        continue
      }
      materialized += day.materializedJobs.length
      projected += day.projectedOccurrences.length
    }
    return { materialized, projected, total: materialized + projected }
  }, [monthSchedule, visibleMonth])

  const markedDates = useMemo(
    () => monthSchedule.map((day) => new Date(`${day.dateKey}T12:00:00`)),
    [monthSchedule],
  )

  const recurringMarkedDates = useMemo(
    () => monthSchedule
      .filter((day) => day.projectedOccurrences.length > 0 || countRecurringJobs(day.materializedJobs) > 0)
      .map((day) => new Date(`${day.dateKey}T12:00:00`)),
    [monthSchedule],
  )

  return {
    monthSchedule,
    scheduleByDate,
    monthStats,
    markedDates,
    recurringMarkedDates,
  }
}

export function JobsCalendarView({
  jobs,
  workDayEntries = [],
  selectedDate,
  onSelectDate,
  onVisibleMonthChange,
  isLoading = false,
  serviceInstanceId,
  assignmentReturnPath,
  relativeTimeNow,
  onOpen,
  onOpenJobById,
  onChat,
  onDelete,
  onOrderSelect,
  onStatusApplied,
  lockMutation,
}: JobsCalendarViewProps) {
  const labels = useWorkspaceLabels()
  const [layout, setLayout] = useState<JobsCalendarLayout>(() => readStoredLayout())
  const [visibleMonth, setVisibleMonth] = useState(
    () => new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1),
  )

  const {
    scheduleByDate,
    monthStats,
    markedDates,
    recurringMarkedDates,
    monthSchedule,
  } = useCalendarSchedule(jobs, visibleMonth)

  const gridDays = useMemo(() => getCalendarGridDays(visibleMonth), [visibleMonth])
  const monthBounds = useMemo(() => getCalendarMonthGridBounds(visibleMonth), [visibleMonth])

  const workDayEntriesByDate = useMemo(() => {
    const map = new Map<string, WorkDayEntry[]>()
    const gridStart = toJobCalendarDateKey(monthBounds.gridStart)
    const gridEnd = toJobCalendarDateKey(monthBounds.gridEnd)

    for (const entry of workDayEntries) {
      const dateKey = resolveWorkDayEntryDateKey(entry)
      if (!dateKey) {
        continue
      }
      if (dateKey < gridStart || dateKey > gridEnd) {
        continue
      }
      const current = map.get(dateKey)
      if (current) {
        current.push(entry)
      } else {
        map.set(dateKey, [entry])
      }
    }

    return map
  }, [workDayEntries, monthBounds.gridStart, monthBounds.gridEnd])

  const selectedDaySchedule = useMemo(
    () => getCalendarDaySchedule(monthSchedule, selectedDate),
    [monthSchedule, selectedDate],
  )

  const selectedDateJobs = selectedDaySchedule.materializedJobs
  const selectedProjectedOccurrences = selectedDaySchedule.projectedOccurrences
  const selectedDateWorkDayEntries = workDayEntriesByDate.get(toJobCalendarDateKey(selectedDate)) ?? []

  const workDayEntriesThisMonth = useMemo(() => {
    let count = 0
    for (const [dateKey, entries] of workDayEntriesByDate.entries()) {
      const dayDate = new Date(`${dateKey}T12:00:00`)
      if (dayDate.getMonth() === visibleMonth.getMonth() && dayDate.getFullYear() === visibleMonth.getFullYear()) {
        count += entries.length
      }
    }
    return count
  }, [workDayEntriesByDate, visibleMonth])

  useEffect(() => {
    window.localStorage.setItem(JOBS_CALENDAR_LAYOUT_STORAGE_KEY, layout)
  }, [layout])

  const handleMonthChange = (month: Date) => {
    const normalized = new Date(month.getFullYear(), month.getMonth(), 1)
    setVisibleMonth(normalized)
    onVisibleMonthChange?.(normalized)
  }

  const detailPanelProps = {
    selectedDate,
    selectedDateJobs,
    selectedProjectedOccurrences,
    selectedWorkDayEntries: selectedDateWorkDayEntries,
    isLoading,
    serviceInstanceId,
    assignmentReturnPath,
    relativeTimeNow,
    onOpen,
    onOpenJobById,
    onChat,
    onDelete,
    onOrderSelect,
    onStatusApplied,
    lockMutation,
  }

  if (layout === 'split') {
    return (
      <div className="grid gap-4 xl:grid-cols-[minmax(340px,400px)_minmax(0,1fr)] xl:items-start">
        <Card className="overflow-hidden rounded-[28px] border-border/70 bg-card/90 shadow-sm xl:sticky xl:top-4">
          <CardHeader className="border-b border-border/60 bg-muted/20 pb-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <CalendarDays className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-lg font-semibold">Calendar</CardTitle>
                  <CardDescription>
                    {labels.entityPlural} by start date · {monthStats.total} this month
                    {monthStats.projected > 0 ? ` · ${monthStats.projected} recurring slots` : ''}
                    {workDayEntriesThisMonth > 0 ? ` · ${workDayEntriesThisMonth} ${labels.workDayEntryPluralLower}` : ''}
                  </CardDescription>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={() => setLayout('full')}
              >
                <Maximize2 className="mr-1.5 h-3.5 w-3.5" />
                Full calendar
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2 p-0">
            <div className="flex justify-center">
              <Calendar
                mode="single"
                month={visibleMonth}
                onMonthChange={handleMonthChange}
                selected={selectedDate}
                onSelect={(date) => {
                  if (date) {
                    onSelectDate(date)
                  }
                }}
                modifiers={{
                  hasSchedule: markedDates,
                  hasRecurringJobs: recurringMarkedDates,
                }}
                modifiersClassNames={{
                  hasSchedule: 'font-semibold',
                  hasRecurringJobs: 'font-semibold',
                }}
                components={{
                  DayButton: (props) => {
                    const dateKey = toJobCalendarDateKey(props.day.date)
                    const daySchedule = scheduleByDate.get(dateKey)
                    return (
                      <CompactCalendarDayButton
                        {...props}
                        materializedCount={daySchedule?.materializedJobs.length ?? 0}
                        projectedCount={daySchedule?.projectedOccurrences.length ?? 0}
                        recurringCount={countRecurringJobs(daySchedule?.materializedJobs ?? [])}
                      />
                    )
                  },
                }}
              />
            </div>
            <CalendarLegend createdEntityLegend={labels.createdEntityLegend} />
          </CardContent>
        </Card>

        <SelectedDayDetailPanel
          {...detailPanelProps}
          emptyHint="Try another day or open the full calendar."
        />
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <Card className="overflow-hidden rounded-[28px] border-border/70 bg-card/90 shadow-sm">
        <CardHeader className="border-b border-border/60 bg-muted/20 pb-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <CalendarDays className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-lg font-semibold">Full calendar</CardTitle>
                <CardDescription>
                  {monthStats.total} scheduled item{monthStats.total === 1 ? '' : 's'} this month
                  {monthStats.projected > 0 ? ` · ${monthStats.projected} recurring slots` : ''}
                  {workDayEntriesThisMonth > 0 ? ` · ${workDayEntriesThisMonth} ${labels.workDayEntryPluralLower}` : ''}
                </CardDescription>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 sm:justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setLayout('split')}
              >
                <LayoutPanelLeft className="mr-1.5 h-3.5 w-3.5" />
                Compact view
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setLayout('split')}
              >
                <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
                Back
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0"
                onClick={() => handleMonthChange(shiftVisibleMonth(visibleMonth, -1))}
                aria-label="Previous month"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <p className="min-w-[10rem] text-center text-sm font-semibold text-foreground">
                {visibleMonth.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
              </p>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0"
                onClick={() => handleMonthChange(shiftVisibleMonth(visibleMonth, 1))}
                aria-label="Next month"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  const today = new Date()
                  handleMonthChange(today)
                  onSelectDate(today)
                }}
              >
                Today
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-2 p-0">
          {isLoading ? (
            <div className="h-[36rem] animate-pulse rounded-2xl bg-muted/40" />
          ) : (
            <div className="overflow-hidden rounded-2xl border border-border/70">
              <div className="grid grid-cols-7 border-b border-border/70 bg-muted/20">
                {WEEKDAY_LABELS.map((label) => (
                  <div
                    key={label}
                    className="px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
                  >
                    {label}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7">
                {gridDays.map((date) => {
                  const dateKey = toJobCalendarDateKey(date)
                  const daySchedule = scheduleByDate.get(dateKey)
                  const events = buildDayEvents(
                    daySchedule?.materializedJobs ?? [],
                    daySchedule?.projectedOccurrences ?? [],
                    workDayEntriesByDate.get(dateKey) ?? [],
                    labels.entity,
                  )

                  return (
                    <FullCalendarDayCell
                      key={dateKey}
                      date={date}
                      visibleMonth={visibleMonth}
                      isSelected={toJobCalendarDateKey(selectedDate) === dateKey}
                      events={events}
                      onSelectDate={onSelectDate}
                      onOpenJob={onOpen}
                      onOpenJobById={onOpenJobById}
                    />
                  )
                })}
              </div>
            </div>
          )}

          <CalendarLegend createdEntityLegend={labels.createdEntityLegend} />
          <p className="text-[11px] text-muted-foreground">
            Showing {toJobCalendarDateKey(monthBounds.gridStart)} to {toJobCalendarDateKey(monthBounds.gridEnd)}.
          </p>
        </CardContent>
      </Card>

      <SelectedDayDetailPanel
        {...detailPanelProps}
        emptyHint="Select another day in the full calendar above."
      />
    </div>
  )
}
